import { useState, useEffect, useCallback } from 'react';
import type { GalleryItem } from '@/mocks/galleryData';

interface LightboxModalProps {
  item: GalleryItem | null;
  onClose: () => void;
}

export default function LightboxModal({ item, onClose }: LightboxModalProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [item]);

  const handlePrev = useCallback(() => {
    if (!item) return;
    setActiveIndex((i) => (i - 1 + item.images.length) % item.images.length);
  }, [item]);

  const handleNext = useCallback(() => {
    if (!item) return;
    setActiveIndex((i) => (i + 1) % item.images.length);
  }, [item]);

  useEffect(() => {
    if (!item) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [item, onClose, handlePrev, handleNext]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl mx-4 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 w-9 h-9 flex items-center justify-center text-white/80 hover:text-white transition-colors cursor-pointer"
          aria-label="닫기"
        >
          <i className="ri-close-line text-2xl" />
        </button>

        {/* Main image */}
        <div className="relative w-full aspect-[4/3] bg-black rounded-lg overflow-hidden">
          <img
            src={item.images[activeIndex]}
            alt={`이미지 ${activeIndex + 1}`}
            className="w-full h-full object-contain"
          />

          {/* Prev / Next arrows */}
          {item.images.length > 1 && (
            <>
              <button
                onClick={handlePrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/70 text-white transition-colors cursor-pointer"
                aria-label="이전 이미지"
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/70 text-white transition-colors cursor-pointer"
                aria-label="다음 이미지"
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            </>
          )}

          {/* Counter */}
          <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
            {activeIndex + 1} / {item.images.length}
          </div>
        </div>

        {/* Thumbnails */}
        <div className="flex gap-2 justify-center flex-wrap">
          {item.images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className={`w-14 h-14 rounded-md overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${
                activeIndex === idx
                  ? 'border-yellow-400 opacity-100'
                  : 'border-transparent opacity-50 hover:opacity-80'
              }`}
            >
              <img
                src={img}
                alt={`썸네일 ${idx + 1}`}
                className="w-full h-full object-cover object-top"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
